import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LOCALES, type Locale } from '@rozman/shared';
import { applyLocale, detectLocale } from '@/lib/i18n';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { CartDrawer } from '@/components/CartDrawer';
import { CookieBanner } from '@/components/CookieBanner';
import { HomePage } from '@/pages/HomePage';
import { CategoryPage } from '@/pages/CategoryPage';
import { ProductPage } from '@/pages/ProductPage';
import { SearchPage } from '@/pages/SearchPage';
import { FabricsPage } from '@/pages/FabricsPage';
import { ContentPage } from '@/pages/ContentPage';
import { CheckoutPage } from '@/pages/CheckoutPage';
import { OrderConfirmationPage } from '@/pages/OrderConfirmationPage';
import { AccountPage } from '@/pages/AccountPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import type { JSX } from 'react';

/** Keeps i18next, `<html lang>` and localStorage in step with the URL segment. */
function LocaleShell(): JSX.Element {
  const { locale } = useParams<{ locale: string }>();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (locale && LOCALES.includes(locale as Locale) && i18n.language !== locale) {
      applyLocale(locale as Locale);
    }
  }, [locale, i18n]);

  if (!locale || !LOCALES.includes(locale as Locale)) return <Navigate to={`/${detectLocale('')}`} replace />;

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main id="main" className="flex-1">
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="kategoria/:slug" element={<CategoryPage />} />
          <Route path="produkt/:slug" element={<ProductPage />} />
          <Route path="szukaj" element={<SearchPage />} />
          <Route path="tkaniny" element={<FabricsPage />} />
          <Route path="strona/:slug" element={<ContentPage />} />
          <Route path="zamowienie" element={<CheckoutPage />} />
          <Route path="zamowienie/:number" element={<OrderConfirmationPage />} />
          <Route path="konto/*" element={<AccountPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <CartDrawer />
      <CookieBanner />
    </div>
  );
}

function ScrollToTop(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);
  return null;
}

export function App(): JSX.Element {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Every page lives under a locale segment, so both are linkable and indexable. */}
        <Route path="/:locale/*" element={<LocaleShell />} />
        <Route path="*" element={<Navigate to={`/${detectLocale(window.location.pathname)}`} replace />} />
      </Routes>
    </>
  );
}
